/**
 * Unified Icon Hook
 * Provides a consistent icon loading mechanism for all travel guide sections
 * Uses icon libraries (react-icons) instead of API calls
 * Priority: API icon URL → Travel Mode Icon → Amenity Icon → Hardcoded SVG → Icon Library (react-icons) → Fallback
 */

import { useMemo } from 'react';
import { renderActivityIcon } from '@/utils/activityIconMapper';
import {
  getTravelModeIconName,
  getAmenityIconName,
  getActivityIconComponent,
  getAmenityIconComponent,
} from '@/utils/iconMapping';
import { getTravelIcon } from '../components/chat/place-details/place-overlay/utils/iconUtils';
import { typographyColors } from '@/design-system/utils';
import React from 'react';
import { Zap, Star, Activity, MapPin, Circle } from 'lucide-react';

// Hardcoded SVG icons for amenities (from AmenitiesAndFacilitiesSection)
const getHardcodedAmenityIcon = (
  iconKey: string | null
): React.ReactNode | null => {
  if (!iconKey) return null;

  const icons: Record<string, React.ReactNode> = {
    'clean-hands': (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <path
          d='M3.33333 10C3.33333 10 4.16667 8.33333 6.66667 8.33333C9.16667 8.33333 10.8333 10 13.3333 10C15.8333 10 16.6667 8.33333 16.6667 8.33333V15C16.6667 15 15.8333 16.6667 13.3333 16.6667C10.8333 16.6667 9.16667 15 6.66667 15C4.16667 15 3.33333 16.6667 3.33333 16.6667V10Z'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M3.33333 5C3.33333 5 4.16667 3.33333 6.66667 3.33333C9.16667 3.33333 10.8333 5 13.3333 5C15.8333 5 16.6667 3.33333 16.6667 3.33333'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    water: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <path
          d='M10 2.5C10 2.5 4.16667 8.33333 4.16667 11.6667C4.16667 14.8883 6.77834 17.5 10 17.5C13.2217 17.5 15.8333 14.8883 15.8333 11.6667C15.8333 8.33333 10 2.5 10 2.5Z'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    toilet: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <path
          d='M7.5 5.83333V2.5H12.5V5.83333M5 5.83333H15C15.4603 5.83333 15.8333 6.20643 15.8333 6.66667V9.16667C15.8333 12.3883 13.2217 15 10 15C6.77834 15 4.16667 12.3883 4.16667 9.16667V6.66667C4.16667 6.20643 4.53976 5.83333 5 5.83333Z'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M7.5 15V17.5M12.5 15V17.5'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    swimming: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <path
          d='M2.5 13.3333C2.5 13.3333 3.75 12.5 5.41667 12.5C7.08333 12.5 8.33333 13.3333 10 13.3333C11.6667 13.3333 12.9167 12.5 14.5833 12.5C16.25 12.5 17.5 13.3333 17.5 13.3333M2.5 16.6667C2.5 16.6667 3.75 15.8333 5.41667 15.8333C7.08333 15.8333 8.33333 16.6667 10 16.6667C11.6667 16.6667 12.9167 15.8333 14.5833 15.8333C16.25 15.8333 17.5 16.6667 17.5 16.6667'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M14.1667 5.83333C15.0871 5.83333 15.8333 5.08714 15.8333 4.16667C15.8333 3.24619 15.0871 2.5 14.1667 2.5C13.2462 2.5 12.5 3.24619 12.5 4.16667C12.5 5.08714 13.2462 5.83333 14.1667 5.83333Z'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M12.5 6.66667L10 10L7.5 8.33333L4.16667 10.8333'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    medical: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <path
          d='M7.5 2.5H12.5C13.4205 2.5 14.1667 3.24619 14.1667 4.16667V15.8333C14.1667 16.7538 13.4205 17.5 12.5 17.5H7.5C6.57953 17.5 5.83333 16.7538 5.83333 15.8333V4.16667C5.83333 3.24619 6.57953 2.5 7.5 2.5Z'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M10 7.5V12.5M7.5 10H12.5'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    gym: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <path
          d='M3.33333 10H16.6667M3.33333 10V8.33333C3.33333 7.87309 3.70643 7.5 4.16667 7.5H5.83333C6.29357 7.5 6.66667 7.87309 6.66667 8.33333V11.6667C6.66667 12.1269 6.29357 12.5 5.83333 12.5H4.16667C3.70643 12.5 3.33333 12.1269 3.33333 11.6667V10ZM16.6667 10V8.33333C16.6667 7.87309 16.2936 7.5 15.8333 7.5H14.1667C13.7064 7.5 13.3333 7.87309 13.3333 8.33333V11.6667C13.3333 12.1269 13.7064 12.5 14.1667 12.5H15.8333C16.2936 12.5 16.6667 12.1269 16.6667 11.6667V10Z'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M6.66667 10H13.3333'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    accommodation: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <path
          d='M2.5 17.5V7.5C2.5 6.57953 3.24619 5.83333 4.16667 5.83333H15.8333C16.7538 5.83333 17.5 6.57953 17.5 7.5V17.5'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M2.5 10H17.5M5.83333 10V17.5M14.1667 10V17.5M10 10V17.5'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M10 2.5L12.5 5.83333H7.5L10 2.5Z'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    restaurants: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <path
          d='M5.83333 10V17.5M8.33333 10V17.5M10.8333 10V17.5M13.3333 10V17.5'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M5.83333 10C5.83333 8.61929 6.95262 7.5 8.33333 7.5H11.6667C13.0474 7.5 14.1667 8.61929 14.1667 10'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M8.33333 7.5V4.16667C8.33333 3.24619 9.07953 2.5 10 2.5C10.9205 2.5 11.6667 3.24619 11.6667 4.16667V7.5'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M5.83333 2.5L5.83333 5.83333M14.1667 2.5L14.1667 5.83333'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    transportation: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <path
          d='M2.5 6.66667C2.5 5.74619 3.24619 5 4.16667 5H15.8333C16.7538 5 17.5 5.74619 17.5 6.66667V13.3333C17.5 14.2538 16.7538 15 15.8333 15H4.16667C3.24619 15 2.5 14.2538 2.5 13.3333V6.66667Z'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M5 15V17.5M15 15V17.5M2.5 10H17.5M5.83333 5V10M14.1667 5V10'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    clock: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <circle
          cx='10'
          cy='10'
          r='7.5'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M10 5V10L13.3333 13.3333'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
  };

  return icons[iconKey] || null;
};

// Distinct fallback icons so different contents don't all show the same icon
const FALLBACK_ICONS = [Zap, Star, Activity, MapPin, Circle] as const;

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h = h & h;
  }
  return Math.abs(h);
}

export interface UseUnifiedIconOptions {
  label: string;
  icon?: string | null;
  id?: string;
  iconSize?: number;
  type?: 'travel-mode' | 'amenity' | 'activity' | 'auto'; // Type hint for better matching
}

export interface UseUnifiedIconResult {
  icon: React.ReactNode | string | null;
}

/**
 * Unified icon hook for all travel guide sections
 * Uses icon libraries (react-icons) instead of API calls
 * Priority: API icon URL → Travel Mode Icon → Amenity Icon → Hardcoded SVG → Icon Library (react-icons) → Fallback
 */
export function useUnifiedIcon({
  label,
  icon: apiIcon,
  id,
  iconSize = 20,
  type = 'auto',
}: UseUnifiedIconOptions): UseUnifiedIconResult {
  // Determine icon with priority order (no API calls)
  const icon = useMemo(() => {
    // Priority 1: API icon (URL from API response) - only if it's a valid URL
    // BUT: For specific known titles, ignore API icon URLs and use library icons instead
    // This prevents incorrect API icons from overriding correct library mappings
    const normalizedLabel = label?.toLowerCase().trim() || '';
    const knownTitlesToIgnoreApiIcon = [
      'weather',
      'temperature',
      'temp',
      'sunrise',
      'sunset',
      'timezone',
      'air pressure',
      'pressure',
      'humidity',
      'currency',
      'population',
      'air quality',
      'police',
      'ambulance',
      'fire brigade',
      'fire department',
    ];

    const shouldIgnoreApiIcon = knownTitlesToIgnoreApiIcon.some(
      title => normalizedLabel === title || normalizedLabel.includes(title)
    );

    console.log('🔍 [useUnifiedIcon] Icon Resolution:', {
      label,
      normalizedLabel,
      apiIcon,
      id,
      type,
      shouldIgnoreApiIcon,
    });

    // Only use API icon if it's valid AND we're not ignoring it for this title
    if (
      !shouldIgnoreApiIcon &&
      apiIcon &&
      typeof apiIcon === 'string' &&
      apiIcon.trim() !== '' &&
      (apiIcon.startsWith('http') || apiIcon.startsWith('/'))
    ) {
      console.log('✅ [useUnifiedIcon] Using API icon URL:', apiIcon);
      return apiIcon;
    }

    if (shouldIgnoreApiIcon) {
      console.log(
        '⏭️ [useUnifiedIcon] Ignoring API icon for known title, using library icon'
      );
    }

    // Priority 2: Travel Mode Icons (if type is travel-mode or id matches travel mode patterns)
    if (
      type === 'travel-mode' ||
      (id &&
        ['air', 'train', 'bus', 'car', 'flight'].includes(id.toLowerCase()))
    ) {
      const travelIconName = getTravelModeIconName(id || apiIcon || label);
      if (travelIconName) {
        const travelIcon = getTravelIcon(travelIconName);
        if (travelIcon) {
          return travelIcon;
        }
      }
    }

    // Priority 3: Amenity Icons (if type is amenity or id matches amenity patterns)
    if (type === 'amenity' || (id && getAmenityIconName(id))) {
      // Try react-icons first for amenities by id
      const AmenityIconResult = getAmenityIconComponent(id);
      if (AmenityIconResult) {
        // If it's a string (SVG path), return it directly
        if (typeof AmenityIconResult === 'string') {
          console.log(
            '✅ [useUnifiedIcon] Priority 3: Found amenity icon SVG path:',
            AmenityIconResult
          );
          return AmenityIconResult;
        }
        // Otherwise it's a component
        console.log(
          '✅ [useUnifiedIcon] Priority 3: Found amenity icon component'
        );
        return (
          <AmenityIconResult size={iconSize} color={typographyColors.primary} />
        );
      }

      // Fallback to hardcoded SVG by id
      const amenityIconName = getAmenityIconName(id);
      if (amenityIconName) {
        const hardcodedIcon = getHardcodedAmenityIcon(amenityIconName);
        if (hardcodedIcon) {
          return hardcodedIcon;
        }
      }

      // When id did not match: try label-based icon so different amenities get distinct icons
      if (type === 'amenity' && label) {
        const LabelIconResult = getActivityIconComponent(label);
        if (LabelIconResult) {
          if (typeof LabelIconResult === 'string') return LabelIconResult;
          const LabelIcon = LabelIconResult;
          return <LabelIcon size={iconSize} color={typographyColors.primary} />;
        }
      }
    }

    // Priority 4: Activity Icons (if type is activity or for activity labels)
    if (type === 'activity' || (!id && label)) {
      console.log(
        '🎯 [useUnifiedIcon] Priority 4: Checking activity icons for:',
        label
      );
      let ActivityIconResult = getActivityIconComponent(label);
      if (!ActivityIconResult && id) {
        ActivityIconResult = getActivityIconComponent(id);
      }
      if (ActivityIconResult) {
        // If it's a string (SVG path), return it directly
        if (typeof ActivityIconResult === 'string') {
          console.log(
            '✅ [useUnifiedIcon] Priority 4: Found activity icon SVG path for:',
            label
          );
          return ActivityIconResult;
        }
        // Otherwise it's a component
        console.log(
          '✅ [useUnifiedIcon] Priority 4: Found activity icon component for:',
          label
        );
        return (
          <ActivityIconResult
            size={iconSize}
            color={typographyColors.primary}
          />
        );
      }
      console.log(
        '❌ [useUnifiedIcon] Priority 4: No activity icon component found for:',
        label
      );
    } else {
      console.log(
        '⏭️ [useUnifiedIcon] Priority 4: Skipped (type:',
        type,
        ', id:',
        id,
        ', label:',
        label,
        ')'
      );
    }

    // Priority 5: Hardcoded amenity SVG (if ID is provided but not matched above)
    if (id) {
      const hardcodedIcon = getHardcodedAmenityIcon(id);
      if (hardcodedIcon) {
        return hardcodedIcon;
      }
    }

    // Priority 6: Icon library (react-icons via activityIconMapper)
    // This is the primary source for most icons
    console.log('📚 [useUnifiedIcon] Trying library icon for label:', label);
    const libraryIcon = renderActivityIcon(
      label,
      apiIcon || null,
      iconSize,
      undefined,
      typographyColors.primary
    );
    if (libraryIcon) {
      console.log('✅ [useUnifiedIcon] Found library icon for:', label);
      return libraryIcon;
    }
    console.log('❌ [useUnifiedIcon] No library icon found for:', label);

    // Priority 7: Distinct fallback by label/id so different contents get different icons
    const seed = (label || id || '').trim() || 'default';
    const index = hashString(seed) % FALLBACK_ICONS.length;
    const FallbackIconComponent = FALLBACK_ICONS[index];
    return (
      <FallbackIconComponent size={iconSize} color={typographyColors.primary} />
    );
  }, [apiIcon, label, id, iconSize, type]);

  return {
    icon,
  };
}
